import cloneDeep from 'lodash/cloneDeep';

import {reporter} from 'common/report/error-reporting';
import {customPropertyClient} from 'common/report/queries/app-api/custom-properties-client';
import {getOwnAttr} from 'common/util';
import {PropertyType} from 'common/report/constants';

import {Store} from './store';

export enum CustomPropertyResourceType {
  Events = `events`,
  People = `people`,
}

export interface ComposedProperties {
  [variable: string]: any; // PropertyItem
}

export interface CustomProperty {
  name: string;
  description: string;
  exampleValue?: string;
  resourceType?: CustomPropertyResourceType;
  displayFormula?: string;
  composedProperties?: ComposedProperties;
  behavior?: any; // QueryBehaviorEntry
  mappedDataGroupId?: string;
  // ReferencedBy is generated on the backend and can be utilized
  // to avoid cyclical references
  referencedDirectlyBy?: number[];
  referencedBy?: number[];
  referencedRawEventProperties?: string[];

  propertyType?: PropertyType;
  joinPropertyType?: PropertyType;
  user?: number; // TODO @evnp - eliminate this confusing attr
  userId?: number;
  userName?: string;
  userEmail?: string;

  // customPropertiesStore holds the assumption that key (customPropertyId) of a
  // customProperty never changes, but this is no longer true now that we give
  // an inline custom properties a session only ID, from which the user can
  // convert to a real customPropertyId by 'save permanently'. We therefore need
  // a way to communicate to listeners of the store that the old Id is invalid
  // and here is the new Id
  newCustomPropertyId?: number;

  // permissions
  can_view?: boolean;
  canUpdateBasic?: boolean;
  can_share?: boolean;
  is_superadmin?: boolean;
  allow_staff_override?: boolean;

  // sharing-disabled (visibility controls in create-entity-modal)
  isVisible?: boolean;
  isLocked?: boolean;
  canUpdateLocked?: boolean;
  canUpdateVisible?: boolean;
  canUpdateRestricted?: boolean;
  canUpdateVisibility?: boolean;
}

export interface CustomProperties {
  [customPropertyId: string]: CustomProperty;
}

export const customPropertiesStore = new Store<CustomProperties>({
  apiClient: customPropertyClient,
  name: `custom-properties`,
  transform: {
    index: (response) =>
      response.results.reduce(
        (accumulater, currentValue) => ({
          ...accumulater,
          ...{[currentValue.customPropertyId]: migrateBehavioralPropertyDefinition(currentValue)},
        }),
        {},
      ),
    update: (response) => {
      const results = response.results;
      return {k: results && results.customPropertyId, v: results};
    },
  },

  defaultValue: {},
});

export function migrateBehavioralPropertyDefinition(property: CustomProperty) {
  // Pre-release versions of behavioral properties used to store behavioral
  // properties as a single composed property within a formula property whose
  // formula was always 'A'. Not many customers were given access to the feature
  // when it used this format, and persisted entities have been updated to
  // the new format, so this migration code only exists to help with inline
  // behavioral properties persisted in URLs. It will probably not be necessary
  // after a few months.
  if (
    property.displayFormula &&
    // it would be nice to check that displayFormula === `A` here, but it seems like
    // som historical properties include comments in the formula
    property.composedProperties &&
    Object.keys(property.composedProperties).length === 1 &&
    getOwnAttr(property.composedProperties, `A`) &&
    getOwnAttr(property.composedProperties[`A`], `behavior`)
  ) {
    // TODO: remove this migration when there are no more logged instances
    reporter.info(`behavioral property format migrated`, {property});

    property = cloneDeep(property);
    property.behavior = property.composedProperties[`A`].behavior;
    delete property.displayFormula;
    delete property.composedProperties;
  }
  return property;
}
